import React from 'react';
import PropTypes from 'prop-types';
import { getDataFromTree } from '@apollo/client/react/ssr';
import initApollo from './initApollo';
import { getActiveLocalFromContext } from '../locales';
import { getAnalyticCookiesFromContext } from '../lib/analyticsProviders/analytics';

/**
 * Settings for the API URL are currently located inside the initApollo.js file
 * This file exists to provide the apolloClient as a prop so we can wrap our Application
 * inside the ApolloProvider. The ApolloProvider has to be wrapped under the Next <Container> or
 * we run into issues using SSR.
 */
export const withApollo = (App) => {
  class WithData extends React.Component {
    constructor(props) {
      super(props);
      // `getDataFromTree` renders the component first, the client is passed off as a property.
      // After that rendering is done using Next's normal rendering pipeline
      this.apolloClient = initApollo(props.apolloState, { analyticIDs: props.analyticIDs, locale: props.locale });
    }

    static async getInitialProps(ctx) {
      const {
        Component,
        router,
        ctx: { res }
      } = ctx;

      const analyticIDs = getAnalyticCookiesFromContext(ctx);
      const locale = getActiveLocalFromContext(ctx);
      const apollo = initApollo({}, { locale, analyticIDs });

      ctx.ctx.apolloClient = apollo;

      let appProps = {};
      if (App.getInitialProps) {
        appProps = await App.getInitialProps(ctx);
      }

      if (res && res.finished) {
        // When redirecting, the response is finished.
        // No point in continuing to render
        return {};
      }

      /**
       * This "process.browser" has been added and is not part of the original Apollo examples
       * provided by Next. If you're tracing back problems and have landed here you may want to
       * remove it or ask James or Evan some questions.
       *
       * This code seems to be required for server side rendering as it consolidates all queries
       * used in the tree in to a single query the page is then rendered the page with the results.
       *
       * On the client we're finding that this consolidated query ends up running but at the same time
       * each individual still runs so the exact same queries queries are being run multiple times.
       * We also found that it has some strange effects on the life cycle of a pages (construct, render,
       * construct, render mount). The first construct and render are for "getDataFromTree" and for
       * some reason don't include query parameters from the route. The following construct, render and
       * mount are when the page is actually being rendered out.
       */
      if (!process.browser) {
        // Run all graphql queries in the component tree
        // and extract the resulting data
        try {
          // Run all GraphQL queries
          await getDataFromTree(<App {...appProps} Component={Component} router={router} apolloClient={apollo} />);
        } catch (error) {
          // Prevent Apollo Client GraphQL errors from crashing SSR.
          // Handle them in components via the data.error prop:
          // http://dev.apollodata.com/react/api-queries.html#graphql-query-data-error
          console.error('GRAPHQL ERROR Error while running `getDataFromTree`'); // eslint-disable-line
          console.error(error); // eslint-disable-line
        }
      }

      // Extract query data from the Apollo's store
      const apolloState = apollo.cache.extract();

      return {
        ...appProps,
        apolloState,
        locale,
        analyticIDs
      };
    }

    render() {
      return <App {...this.props} apolloClient={this.apolloClient} />;
    }
  }

  WithData.displayName = `WithData(${App.displayName})`;

  WithData.propTypes = {
    apolloState: PropTypes.object.isRequired, //eslint-disable-line
    analyticIDs: PropTypes.shape({}),
    locale: PropTypes.string
  };

  WithData.defaultProps = {
    analyticIDs: null,
    locale: ''
  };

  return WithData;
};
